import apiClient from './api';

export interface Avatar {
  _id: string;
  id: string;
  name: string;
  description?: string;
  imageUrl: string;
  originalName: string;
  mimeType: string;
  size: number;
  isLibrary: boolean;
  tags: string[];
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AvatarListResponse {
  success: boolean;
  avatars: {
    own: Avatar[];
    library: Avatar[];
  };
}

export interface AvatarResponse {
  success: boolean;
  avatar: Avatar;
}

const BASE = '/avatars';

export const avatarApi = {
  // List all avatars (own + library)
  getAvatars: async (): Promise<AvatarListResponse> => {
    const res = await apiClient.get(BASE);
    return res.data;
  },

  // Get single avatar
  getAvatar: async (id: string): Promise<AvatarResponse> => {
    const res = await apiClient.get(`${BASE}/${id}`);
    return res.data;
  },

  // Upload a new custom avatar (only allowed while the 'avatar-creation' feature flag is enabled)
  uploadAvatar: async (formData: FormData): Promise<AvatarResponse> => {
    const res = await apiClient.post(BASE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  // Update avatar metadata
  updateAvatar: async (id: string, data: { name?: string; description?: string; tags?: string }): Promise<AvatarResponse> => {
    const res = await apiClient.put(`${BASE}/${id}`, data);
    return res.data;
  },

  // Soft-delete avatar
  deleteAvatar: async (id: string): Promise<{ success: boolean; message: string }> => {
    const res = await apiClient.delete(`${BASE}/${id}`);
    return res.data;
  },

  // Build image URL via /api/avatars/image/:filename — works through nginx proxy
  getImageSrc: (imageUrl: string): string => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    const filename = imageUrl.split('/').pop();
    if (!filename) return '';
    const apiBase = (import.meta as any).env?.VITE_API_URL ?? '/api';
    const base = apiBase.replace(/\/?$/, '');
    return `${base}/avatars/image/${filename}`;
  },
};
